import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { deleteMember, fetchMemberDetail, updateMember } from "../../store/slices/membersSlice";
import { ApprovalStatus } from "../../types/admin";
import {
  approvalStatusTone,
  formatApprovalStatus,
  ownerApprovalOptions,
  roleLabelMap,
  workerApprovalOptions,
} from "../../utils/admin";

const MemberDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError, updating, deleting } = useAppSelector((state) => state.members);

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editApprovalStatus, setEditApprovalStatus] = useState<ApprovalStatus>("심사 대기");
  const [editIsActive, setEditIsActive] = useState(true);

  useEffect(() => {
    if (userId) {
      dispatch(fetchMemberDetail(Number(userId)));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (detail && !isEditing) {
      setEditName(detail.user_name);
      setEditPhone(detail.phone_number);
      setEditApprovalStatus(detail.approval_status);
      setEditIsActive(detail.is_active ?? true);
    }
  }, [detail, isEditing]);

  const approvalEditOptions =
    detail?.role === "worker"
      ? workerApprovalOptions
      : detail?.role === "owner"
        ? ownerApprovalOptions
        : [
            ...ownerApprovalOptions,
            { value: "pending_owner_assignment" as ApprovalStatus, label: "사업주 승인 대기" },
            { value: "approved_by_owner" as ApprovalStatus, label: "사업주 승인 완료" },
          ];

  const handleUpdate = async () => {
    if (!userId || !detail) return;
    try {
      await dispatch(
        updateMember({
          userId: Number(userId),
          user_name: editName !== detail.user_name ? editName : undefined,
          phone_number: editPhone !== detail.phone_number ? editPhone : undefined,
          approval_status: editApprovalStatus !== detail.approval_status ? editApprovalStatus : undefined,
          is_active: editIsActive !== detail.is_active ? editIsActive : undefined,
        })
      ).unwrap();
      window.alert("회원 정보가 성공적으로 수정되었습니다.");
      setIsEditing(false);
    } catch (error) {
      window.alert((error as string) || "수정에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    if (window.confirm("정말로 이 회원을 삭제하시겠습니까?")) {
      try {
        await dispatch(deleteMember(Number(userId))).unwrap();
        window.alert("회원이 삭제되었습니다.");
        navigate("/admin/members", { replace: true });
      } catch (error) {
        window.alert((error as string) || "삭제에 실패했습니다.");
      }
    }
  };

  return (
    <section className="admin-page">
      <header className="detail-header">
        <button className="back-btn" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h2 className="section-title">회원 상세정보</h2>
      </header>

      {loadingDetail && <p className="loading-text">회원 상세를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}

      {detail && (
        <>
          <div className="detail-grid">
            <article className="detail-card">
              <span>이름</span>
              {isEditing ? (
                <input value={editName} onChange={(e) => setEditName(e.target.value)} />
              ) : (
                <strong>{detail.user_name}</strong>
              )}
            </article>
            <article className="detail-card">
              <span>이메일</span>
              <strong>{detail.email}</strong>
            </article>
            <article className="detail-card">
              <span>연락처</span>
              {isEditing ? (
                <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
              ) : (
                <strong>{detail.phone_number}</strong>
              )}
            </article>
            <article className="detail-card">
              <span>분류</span>
              <strong>{roleLabelMap[detail.role]}</strong>
            </article>
            <article className="detail-card">
              <span>승인·가입 상태</span>
              {isEditing ? (
                <select
                  value={editApprovalStatus}
                  onChange={(e) => setEditApprovalStatus(e.target.value as ApprovalStatus)}
                >
                  {approvalEditOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div>
                  <span
                    className={`member-approval-badge member-approval-badge--${approvalStatusTone(
                      detail.approval_status
                    )}`}
                  >
                    {formatApprovalStatus(detail.approval_status)}
                  </span>
                  {detail.role === "owner" && (
                    <p className="owner-cert-sync-hint" style={{ marginTop: 8 }}>
                      사업주 상태는 소유 점포 운영/인증 상태와 동기화됩니다.
                    </p>
                  )}
                </div>
              )}
            </article>
            <article className="detail-card">
              <span>계정 활성화</span>
              {isEditing ? (
                <select
                  value={editIsActive ? "true" : "false"}
                  onChange={(e) => setEditIsActive(e.target.value === "true")}
                >
                  <option value="true">활성</option>
                  <option value="false">비활성 (정지)</option>
                </select>
              ) : (
                <strong>{detail.is_active === false ? "비활성 (정지)" : "활성"}</strong>
              )}
            </article>
            <article className="detail-card">
              <span>소속 지점</span>
              <div className="detail-card__branches">
                {detail.branches.length > 0 ? (
                  detail.branches.map((branch) => (
                    <strong key={branch.branch_id}>{`${branch.branch_name} (${branch.branch_code})`}</strong>
                  ))
                ) : (
                  <strong>-</strong>
                )}
              </div>
            </article>
          </div>

          <div className="detail-actions">
            {isEditing ? (
              <>
                <button className="primary-btn" type="button" onClick={handleUpdate} disabled={updating}>
                  {updating ? "저장 중..." : "저장하기"}
                </button>
                <button className="secondary-btn" type="button" onClick={() => setIsEditing(false)} disabled={updating}>
                  취소
                </button>
              </>
            ) : (
              <>
                <button className="primary-btn" type="button" onClick={() => setIsEditing(true)}>
                  회원정보 수정하기
                </button>
                <button className="secondary-btn danger" type="button" onClick={handleDelete} disabled={deleting}>
                  {deleting ? "삭제 중..." : "회원정보 삭제하기"}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default MemberDetailPage;
